import * as React from 'react'
import { cn } from '../../lib/utils'
import { DetailSectionDefaultsContext } from '../core/detail-section'

/**
 * DetailPanel — the sidebar shell of a detail page: a bordered, scrollable white
 * card that stacks {@link DetailSection}s (Summary, Related Companies, Contacts,
 * Tags …). It's just the chrome — width, padding, border, and a scroll region —
 * so the same panel frames a deal, a company, or a contact; the sections and
 * their bodies (an `InfoList`, a `Tree`, chip clusters) are composed as
 * `children`.
 *
 *   <DetailPanel>
 *     <DetailSection title="Summary"><InfoList>…</InfoList></DetailSection>
 *     <DetailSection title="Related Companies" count={2}>
 *       <Tree nodes={…} onAdd={…} addLabel="Add company" />
 *     </DetailSection>
 *   </DetailPanel>
 *
 * Defaults to the 340px column the app uses; override with a `w-*` class. The
 * card scrolls its own content (`fbui-scroll`) when the parent constrains its
 * height (`h-full` in the page layout); unconstrained, it grows to fit.
 *
 * Sections inside a panel get the in-header `rule` and a heading `level` of 2 by
 * default (via context) so the whole panel reads consistently under a page `h1`;
 * set `rule={false}` / `level` on a section to opt out.
 */

export interface DetailPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  /**
   * How the panel attaches to the page:
   * - `card` (default) — a floating, bordered, rounded card with a drop shadow,
   *   sitting on a canvas with a gutter around it. Right for a list/dashboard
   *   context where the panel is one of several peer cards.
   * - `rail` — a flush, full-height rail attached to the layout edge: no radius
   *   or shadow, a single right hairline divider, and it fills its parent's
   *   height (scrolling internally). Right for a *detail page*, where the panel
   *   is persistent reference chrome beside the work area. Pair with
   *   {@link EntityDetailRail}, which supplies the height-bounded parent.
   */
  variant?: 'card' | 'rail'
}

// Shared chrome for both variants. Width is a default; `EntityDetailRail` (or a
// `w-*` className) can override it. `[&>*+*]:mt-2` spaces stacked sections so
// the gap *between* sections reads larger than the gap between rows *within*
// one — grouping.
const panelBase =
  'fbui-scroll flex w-[340px] flex-col overflow-y-auto bg-fbui-base-white p-4 [&>*+*]:mt-2'

const panelVariantChrome: Record<NonNullable<DetailPanelProps['variant']>, string> = {
  card: 'rounded-lg border border-fbui-gray-200 shadow-fbui-sm',
  // `h-full` fills the height-bounded parent; the right divider is the only
  // separation from the work area (no radius/shadow — this is structural chrome,
  // not a floating object).
  rail: 'h-full border-r border-fbui-gray-200',
}

export const DetailPanel = React.forwardRef<HTMLDivElement, DetailPanelProps>(
  ({ className, children, variant = 'card', ...props }, ref) => (
    <div
      ref={ref}
      className={cn(panelBase, panelVariantChrome[variant], className)}
      {...props}
    >
      <DetailSectionDefaultsContext.Provider value={{ rule: true, level: 2 }}>
        {children}
      </DetailSectionDefaultsContext.Provider>
    </div>
  )
)
DetailPanel.displayName = 'DetailPanel'
